import React, {
  useState, useMemo, useEffect, useCallback,
} from 'react';
import styled, { css } from 'styled-components';
import tw from 'tailwind.macro';
import * as turf from '@turf/turf';
import 'typeface-roboto';

import Sentry from '../utils/sentry';
import {
  parseData, getNearestLocalsFromPoint, getActualTime, getDates,
  shouldShowLocalByFilters, setStatusByFilters,
} from '../utils/helpers';
import * as api from '../utils/api';
import usePosition from '../hooks/useLocation';
import useWindowSize from '../hooks/useWindowSize';

// Components
import '../style/layout.css';
import SEO from '../components/SEO';
import Search from '../components/Search';
import SearchByLocationButton from '../components/SearchByLocationButton';
import Map from '../components/Map';
import Loading from '../components/Loading';
import LocalList from '../components/LocalList';
import Zoom from '../components/Zoom';
import SearchFilters from '../components/SearchFilters';
import ScheduleFilter from '../components/ScheduleFilter';

// Disable console.log for production
const DEBUG = process.env.GATSBY_DEBUG;
if (!DEBUG) {
  // eslint-disable-next-line no-console
  console.log = () => {};
}

export default () => {
  const size = useWindowSize();
  const [mapOrAddressActive, setMapOrAddressActive] = useState(false);
  const [showLoading, setShowLoading] = useState(true);
  const [actualViewportMap, setActualViewportMap] = useState();
  const [actualZoom, setActualZoom] = useState(15);
  const [actualLocation, setActualLocation] = useState();
  const [center, setCenter] = useState();
  const [myLocation, setMyLocation] = useState();
  const [locationType, setLocationType] = useState('geo');
  const [locals, setLocals] = useState([]);
  const [selectedLocalID, setSelectedLocalID] = useState();
  const [showMap, setShowMap] = useState(true);
  const [filters, setFilters] = useState({
    time: getActualTime(),
    date: getDates()[0].value,
    filter: 'all',
  });
  const { latitude: latitudeGPS, longitude: longitudeGPS, error: errorGPS } = usePosition();

  // Return locals that can be visible in viewport
  const localsToShow = useMemo(() => {
    if (!actualViewportMap) return [];

    console.log('Update locals to show according to viewport');

    const polygon = turf.polygon(actualViewportMap);

    return locals
      .filter((local) => turf.booleanPointInPolygon(local.coordenada, polygon))
      .map((local) => setStatusByFilters(local, filters))
      .filter((local) => shouldShowLocalByFilters(local, filters));
  }, [actualViewportMap, locals, filters]);

  // To get nearest locals from specific local
  const nearestLocalsFromPoint = (
    point,
    kms = 10,
    n = null,
    open = false,
  ) => getNearestLocalsFromPoint(locals, point, kms, n, open, filters);

  // Set GPS Location
  const setGPSLocation = useCallback(() => {
    console.log('Trying to set GPS location...', errorGPS, latitudeGPS, longitudeGPS);
    if (errorGPS || !longitudeGPS || !latitudeGPS) return;

    console.log('GPS location ready!', errorGPS, latitudeGPS, longitudeGPS);

    setActualLocation({
      lat: latitudeGPS,
      lng: longitudeGPS,
    });
    setCenter({
      lat: latitudeGPS,
      lng: longitudeGPS,
    });
    

    setLocationType('geo');
  }, [errorGPS, latitudeGPS, longitudeGPS]);

  const searchByAddress = (coordinates) => {
    console.log('Set search by address', coordinates);
    
    if (coordinates) {
      //force to move in address search
      // let tmpCoord = {lat: coordinates.lat + 0.1, lng: coordinates.lng}
      // setActualLocation(tmpCoord);

      setActualLocation(coordinates);
      setCenter(coordinates);
      setMapOrAddressActive(true);
      setActualZoom(15);
      setLocationType('address');
    }
  };

  const onChangeFilter = ({ type, filter }) => {
    setFilters((prev) => ({
      ...prev,
      [type]: filter,
    }));
  };

  const searchByLocation = () => {
    console.log('Set search by GPS location');

    setGPSLocation();
    setMapOrAddressActive(false);
    setActualZoom(15);
  };

  const selectLocalToShow = (localID) => {
    if (!localID) {
      setSelectedLocalID();
      return;
    }
    // Search, select local and center the map on it
    const local = locals.find((local) => local.local === localID);
    if (!local) return;

    setShowMap(true);
    setSelectedLocalID(localID);
    
    setCenter({
      //Actual location with a little margin to make space for the infowindow
      lat: local.coordenadaObj.lat - 0.003,
      lng: local.coordenadaObj.lng + 0.0003,
    });
    //uncomment to hide location if click a local
    //setLocationType();
    setActualZoom(16);
  };

  // Get updated data from API
  const getData = async () => {
    console.log('Getting data...');
    try {
      const [localsResponse, statusesResponse] = await Promise.all([
        api.fetchData(),
        api.fetchStatus(),
      ]);
      const parsedLocals = parseData(
        localsResponse,
        statusesResponse ? statusesResponse.locales : [],
      );
      setLocals(parsedLocals);
    } catch (error) {
      console.error('Error on setting up data');
      Sentry.captureException(error);
    } finally {
      // Bye loading modal (wait at least 2 seconds)
      setTimeout(() => {
        setShowLoading(false);
      }, 2000);
    }
  };

  useEffect(() => {
    // Get data from API
    getData();
  }, []);

  // Search by current location as soon as we get location
  useEffect(() => {
    console.log('Is map active?', mapOrAddressActive);

    // Only search by GPS if map or search address is not active
    if (mapOrAddressActive) return;

    setTimeout(() => {
      setGPSLocation();
    }, 1000);
  }, [setGPSLocation, mapOrAddressActive]);

  // To fix 100vh on mobile view
  const isClient = typeof window === 'object';
  if (isClient) {
    const vh = size.height * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  return (
    <Container>
      <SEO />
      <Loading show={showLoading} />

      <Content>
        <SearchContainer
          searchByAddress={searchByAddress}
          filters={filters}
          onChangeFilter={onChangeFilter}
          showingMap={showMap}
          toggleShowMap={() => setShowMap(!showMap)}
        />

        <LocalListContainer
          locals={localsToShow}
          setSelectedLocalID={selectLocalToShow}
          show={!showMap}
        />

        <MainContent show={showMap}>
          <MapContainer>
            <Map
              locals={localsToShow}
              center={center}
              setCenter={setCenter}
              actualLocation={actualLocation}
              nearestLocalsFromPoint={nearestLocalsFromPoint}
              locationType={locationType}
              zoom={actualZoom}
              setActualViewportMap={setActualViewportMap}
              setMapOrAddressActive={setMapOrAddressActive}
              setZoom={setActualZoom}
              selectedLocalID={selectedLocalID}
              setSelectedLocalID={selectLocalToShow}
            />
          </MapContainer>

          <Zoom zoom={actualZoom} setZoom={setActualZoom} />

          <SearchByLocationButton
            searchByLocation={searchByLocation}
          />
        </MainContent>

        <ScheduleFilter onFilter={onChangeFilter} filters={filters} />
        <Filters onFilter={onChangeFilter} filters={filters} small withoutDateTime />
      </Content>
    </Container>
  );
};

const Container = styled.div`
  font-family: 'Roboto', sans-serif;
`;

const Content = styled.div`
  ${tw`h-screen overflow-auto`};

  display: grid;
  grid-template-areas: 'main'
                       'filters';
  grid-template-columns: 1fr;
  // Hack to fix 100vh on mobile
  grid-template-rows: calc(calc(var(--vh, 1vh) * 100) - 65px) 65px;

  @media (min-width: 768px) {
    grid-template-areas: 'header header'
                         'main sidebar';
    grid-template-columns: 1fr 400px;
    grid-template-rows: auto calc(100vh - 65px);
  }
`;

const SearchContainer = styled(Search)`
  grid-area: header;
`;

const LocalListContainer = styled(LocalList)`
  ${tw`hidden`}

  ${(props) => props.show && css`
    display: flex;
    grid-area: main;
  `}

  @media (min-width: 768px) {
    grid-area: sidebar;
    ${tw`flex`}
  }
`;

const MainContent = styled.div`
  grid-area: main;
  ${tw`relative max-h-full`}

  ${(props) => !props.show && tw`
    hidden
  `}
`;

const MapContainer = styled.div`
  height: 100%;
`;

const Filters = styled(SearchFilters)`
  grid-area: filters;
  border-top: 1px solid #ccc;
  ${tw`
    bg-white
  `}

  @media (min-width: 768px) {
    ${tw`
      hidden
    `};
  }
`;
